
import { Component, Prop, Vue } from 'vue-property-decorator';
import ProductCatalogueRepository from '@/repositories/ProductCatalogueRepository';
import { MaterialTypes } from '@/enums/global/MaterialTypes';
import OfferItem from '@/models/OfferItem';
import { MaterialProperties } from '@/interfaces/general/MaterialProperties';
import ConfiguratorService from '@/services/ConfiguratorService';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import OfferItemRepository from '@/repositories/OfferItemRepository';
import { ChecklistFieldEntry } from '@/interfaces/components/configurator/ChecklistFieldEntry';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { EventBus } from '@/helpers/EventBusHelper';
import { formatFormData } from '@/helpers/FormDataHelper';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import { capitalizeWord } from '@/helpers/TranslationHelper';
import { ProductTypes } from '@/enums/global/ProductTypes';
import { isMaterialColorable } from '@/regex/allowedColorModuleMaterials';
import { ColorPowder } from '@/interfaces/components/color/ColorPowder';
import { Color as ColorInfo } from '@/interfaces/components/color/Color';

@Component({
    name: 'AddMaterialSlot',
})
export default class AddMaterialSlot extends Vue {
    @Prop({ required: true }) private popupData!: any;
    @Prop({ default: false }) private isCalculator!: boolean;

    private materialTypes = MaterialTypes;
    private materialProperties: MaterialProperties = {
        q: null,
        x: null,
        y: null,
    };
    private offerItemDisplayRowNumber: null | number = null;
    private production = true;
    private loadingOverlay = new LoadingOverlayHelper(this, {});
    private svgSource = require('../../../../public/color-picker.svg');

    private selectedColor: ColorInfo | null = null;
    private selectedPowder: ColorPowder | null = null;
    private selectedColorLabel = '';

    private get productCatalogue() {
        return ProductCatalogueRepository.getById(this.popupData.materialId);
    }

    private get isColorable(): boolean {
        return isMaterialColorable(
            this.productCatalogue?.commodityGroup ?? '',
        );
    }

        private get materialType() {
        if (this.productCatalogue == null) {
            return null;
        }
        switch (this.productCatalogue.getProductType) {
            case 0:
                return MaterialTypes.UNIT;
            case 1:
            case 2:
                return MaterialTypes.METER;
            case 3:
            case 4:
                return MaterialTypes.METER_SQUARED;
            default:
                return null;
        }
    }

    private get rowsFilled(): boolean {
        const { q, x, y } = this.materialProperties;

        if (this.materialType === MaterialTypes.UNIT) {
            return q !== null && q !== '';
        }
        if (this.materialType === MaterialTypes.METER) {
            return x !== null && x !== '' && q !== null && q !== '';
        }
        if (this.materialType === MaterialTypes.METER_SQUARED) {
            return x !== null && x !== '' && y !== null && y !== '' && q !== null && q !== '';
        }
        return false;
    }

    private get canSubmit(): boolean {
        if (!this.rowsFilled) return false;
        return true;
    }

    private capitalize(word: string) {
        return capitalizeWord(word);
    }

    private openColorModule() {
        EventBus.$emit(EventBusEvents.showColorModule, {
            colorSystemPID: 'material',
            fieldValue: '',
            activeProductFormId: null,
            productName: this.productCatalogue?.description ?? '',
        });
    }

    private onColorSelected(payload: {
        color: ColorInfo;
        selectedPowder: ColorPowder;
        isRoltekColor: boolean;
        groupName: string;
    }) {
        this.selectedColor = payload.color;
        this.selectedPowder = payload.selectedPowder;
        this.selectedColorLabel = [
            payload.color?.name,
            payload.selectedPowder?.name,
        ].filter(Boolean).join(', ');
    }

    private checkAndShowErrors() {
        const errorMessages: string[] = [];
        if (this.materialProperties.q === null || this.materialProperties.q === '') {
            errorMessages.push(this.$t('Molimo popunite količinu') as string);
        }

        if (this.materialType === MaterialTypes.METER) {
            if (this.materialProperties.x === null || this.materialProperties.x === '') {
                errorMessages.push(this.$t('Molimo popunite x') as string);
            }
        }

        if (this.materialType === MaterialTypes.METER_SQUARED) {
            if (this.materialProperties.x === null || this.materialProperties.x === '') {
                errorMessages.push(this.$t('Molimo popunite x') as string);
            }
            if (this.materialProperties.y === null || this.materialProperties.y === '') {
                errorMessages.push(this.$t('Molimo popunite y') as string);
            }
        }
        if (errorMessages.length > 0) {
            errorMessages.forEach((errorMessage) => {
                this.$notification.error({
                    message: this.$t('Dogodila se greška') as string,
                    description: errorMessage,
                });
            });
            return false;
        }
        return true;
    }

    private async onAddMaterialToOffer() {
        if (!this.checkAndShowErrors()) {
            return;
        }
        this.loadingOverlay.start();

        if (this.isCalculator) {
            await this.updateOrCreateMaterial('');
            return;
        }

        let validOfferId;
        try {
            validOfferId = await ConfiguratorService.getValidOfferId(
                this.popupData.projectId,
                this.popupData.offerId,
                this.popupData.clientsPaymentTypeId,
            );
        } catch (e) {
            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: (e as Error).message,
            });
            this.loadingOverlay.stop();
            return;
        }
        
        this.updateOrCreateMaterial(validOfferId);
    }

    private buildColorData() {
        if (!this.selectedColor && !this.selectedPowder) return null;
        return {
            colorName: this.selectedColor?.name ?? null,
            colorHex: this.selectedColor?.hex ?? null,
            powderId: this.selectedPowder?.id ?? null,
            powderName: this.selectedPowder?.name ?? null,
            ingeniousColor: this.selectedColor?.ingeniousColor ?? null,
        };
    }

    private async updateOrCreateMaterial(offerId: string) {
        const formData = formatFormData(this.materialProperties as ActiveProductFormValueObject);
        const colorData = this.buildColorData();
        try {
            if (this.popupData.offerItemId == null) {
                if (this.isCalculator) {
                    const result = await OfferItem.calculatePrice(
                        formData,
                        this.popupData.materialId,
                        this.offerItemDisplayRowNumber,
                        this.production,
                        colorData,
                    );

                    const attrs = result?.data;
                    if (attrs) {
                        const prices = {
                            purchasePrices: [
                                ...( attrs.purchasePriceDetails ?? []).map((d: any) => ({ priceValue: d.priceDetailAmount, label: d.priceDetailName })),
                                { priceValue: attrs.purchasePriceFinal, label: 'Final' },
                            ],
                            sellingPrices: [
                                ...(attrs.sellingPriceDetails ?? []).map((d: any) => ({ priceValue: d.priceDetailAmount, label: d.priceDetailName })),
                                { priceValue: attrs.sellingPriceFinal, label: 'Final' },
                            ],
                            roltekPrices: [
                                ...(attrs.roltekPriceDetails ?? []).map((d: any) => ({ priceValue: d.priceDetailAmount, label: d.priceDetailName })),
                                { priceValue: attrs.roltekPriceFinal, label: 'Final' },
                            ],
                        };
                        EventBus.$emit(EventBusEvents.calculatorPurchasePrice, prices);
                    }

                    this.onFinalizeMaterialAction();
                    return;
                } else {
                    await OfferItem.createMaterial(
                        formData,
                        this.popupData.materialId,
                        offerId,
                        this.offerItemDisplayRowNumber,
                        this.production,
                        colorData,
                    );
                }
            } else {
                await OfferItem.updateExisting(
                    formData,
                    String(this.popupData.offerItemId),
                    ProductTypes.MATERIAL,
                    false,
                    null,
                    this.offerItemDisplayRowNumber,
                    this.production,
                    null,       
                    null,
                    colorData
                );
            }
        } catch (e) {
            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: (e as Error).message,
            });
            this.loadingOverlay.stop();
            return;
        }
        this.onFinalizeMaterialAction();
    }

    private onFinalizeMaterialAction() {
        EventBus.$emit(EventBusEvents.updateProject);
        EventBus.$emit(EventBusEvents.closePopup);
        this.loadingOverlay.stop();
    }

    private mounted() {
        EventBus.$on(EventBusEvents.selectMaterialColor, this.onColorSelected);

        if (this.popupData.offerItemId == null) return;
        const offerItem = OfferItemRepository.getById(this.popupData.offerItemId);
        if (offerItem == null) return;

        const offerItemWidth = offerItem.offerItemFormData.find((entry: ChecklistFieldEntry) => entry.id === 'x').value;
        const offerItemHeight = offerItem.offerItemFormData.find((entry: ChecklistFieldEntry) => entry.id === 'y')
            .value;
        const offerItemQuantity = offerItem.offerItemFormData.find((entry: ChecklistFieldEntry) => entry.id === 'q')
            .value;

        this.materialProperties.q = offerItemQuantity;
        this.materialProperties.x = offerItemWidth;
        this.materialProperties.y = offerItemHeight;
        this.offerItemDisplayRowNumber = offerItem.connection;
        this.production = offerItem.production;

        const colorData = offerItem.materialColorData;
        if (colorData) {
            this.selectedColorLabel = [colorData.colorName, colorData.powderName]
                .filter(Boolean)
                .join(', ');
            this.selectedColor = { name: colorData.colorName } as ColorInfo;
            this.selectedPowder = { name: colorData.powderName } as ColorPowder;
        }
    }

    private beforeDestroy() {
        EventBus.$off(EventBusEvents.selectMaterialColor, this.onColorSelected);
    }
}
